import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types/user';

interface NavItem {
  key: string;
  path: string;
  icon: string;
  roles: UserRole[];
}

const navItems: NavItem[] = [
  {
    key: 'dashboard',
    path: '/dashboard',
    icon: '🏠',
    roles: ['super_admin', 'admin', 'company_admin', 'driver', 'hr', 'accounting', 'manager'],
  },
  {
    key: 'myVehicle',
    path: '/my-vehicle',
    icon: '🚙',
    roles: ['driver'],
  },
  {
    key: 'trips',
    path: '/trips',
    icon: '🚗',
    roles: ['super_admin', 'admin', 'company_admin', 'driver', 'hr', 'accounting', 'manager'],
  },
  {
    key: 'reports',
    path: '/reports',
    icon: '📊',
    roles: ['super_admin', 'admin', 'company_admin', 'hr', 'accounting', 'manager'],
  },
  {
    key: 'drivers',
    path: '/drivers',
    icon: '👥',
    roles: ['super_admin', 'admin', 'company_admin', 'hr', 'manager'],
  },
  {
    key: 'vehicles',
    path: '/vehicles',
    icon: '🚙',
    roles: ['super_admin', 'admin', 'company_admin', 'manager'],
  },
  {
    key: 'users',
    path: '/users',
    icon: '👤',
    roles: ['super_admin', 'admin', 'company_admin'],
  },
  {
    key: 'settings',
    path: '/settings',
    icon: '⚙️',
    roles: ['super_admin', 'admin', 'company_admin', 'driver', 'hr', 'accounting', 'manager'],
  },
];

const adminNavItems: NavItem[] = [
  {
    key: 'companies',
    path: '/admin/companies',
    icon: '🏢',
    roles: ['super_admin'],
  },
  {
    key: 'deviceInventory',
    path: '/admin/device-inventory',
    icon: '📡',
    roles: ['super_admin'],
  },
  {
    key: 'deviceLogs',
    path: '/admin/device-logs',
    icon: '📋',
    roles: ['super_admin'],
  },
  {
    key: 'maintenance',
    path: '/maintenance',
    icon: '🔧',
    roles: ['super_admin'],
  },
  {
    key: 'webhookEvents',
    path: '/admin/webhook-events',
    icon: '📡',
    roles: ['super_admin'],
  },
  {
    key: 'qualificationRules',
    path: '/qualification-rules',
    icon: '🗂️',
    roles: ['super_admin'],
  },
  {
    key: 'fleetHealth',
    path: '/admin/fleet-health',
    icon: '🛡️',
    roles: ['super_admin'],
  },
];

/**
 * Checks if a user has access to a navigation item based on their role
 */
const hasAccess = (
  userRole: UserRole | undefined,
  allowedRoles: UserRole[],
  isSuperAdmin: boolean
): boolean => {
  if (isSuperAdmin) return true;
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
};

interface SidebarProps {
  /** Whether the mobile drawer is open */
  isMobileOpen: boolean;
  /** Callback to close the mobile drawer */
  onMobileClose: () => void;
}

/** Shared nav content rendered inside both the desktop sidebar and the mobile drawer */
const SidebarContent: React.FC<{ onNavClick?: () => void }> = ({ onNavClick }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin || false;
  const userRole = user?.role;

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => hasAccess(userRole, item.roles, isSuperAdmin));
  }, [userRole, isSuperAdmin]);

  const visibleAdminNavItems = useMemo(() => {
    return adminNavItems.filter((item) => hasAccess(userRole, item.roles, isSuperAdmin));
  }, [userRole, isSuperAdmin]);

  return (
    <nav className="px-3 py-6">
      <div className="mb-4 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {t('nav.navigation')}
      </div>
      <div className="space-y-1">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onNavClick}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
              )
            }
          >
            <span className="text-lg">{item.icon}</span>
            <span>{t(`nav.${item.key}`)}</span>
          </NavLink>
        ))}
      </div>

      {/* Admin Section */}
      {visibleAdminNavItems.length > 0 && (
        <>
          <div className="mt-8 mb-4 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {t('nav.admin')}
          </div>
          <div className="space-y-1">
            {visibleAdminNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onNavClick}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-700 hover:bg-purple-100 hover:text-purple-900'
                  )
                }
              >
                <span className="text-lg">{item.icon}</span>
                <span>{t(`nav.${item.key}`)}</span>
              </NavLink>
            ))}
          </div>
        </>
      )}

      <div className="mt-8 pt-8 border-t border-gray-200">
        <div className="space-y-1">
          <a
            href="#"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 hover:text-gray-900"
          >
            <span className="text-lg">📄</span>
            <span>{t('nav.docs')}</span>
          </a>
          <a
            href="#"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 hover:text-gray-900"
          >
            <span className="text-lg">❓</span>
            <span>{t('nav.help')}</span>
          </a>
        </div>
      </div>
    </nav>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, onMobileClose }) => {
  return (
    <>
      {/* ── Desktop sidebar — hidden on mobile, fixed on lg+ ── */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:top-16 lg:w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto z-30">
        <SidebarContent />
      </aside>

      {/* ── Mobile drawer ── */}
      {/* Backdrop overlay — tap to close */}
      <div
        className={clsx(
          'fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden transition-opacity duration-300',
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        aria-hidden="true"
        onClick={onMobileClose}
      />

      {/* Drawer panel */}
      <aside
        className={clsx(
          'fixed top-0 left-0 bottom-0 z-50 w-72 bg-white shadow-xl lg:hidden flex flex-col overflow-y-auto transform transition-transform duration-300 ease-in-out',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Drawer header with close button */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#0F172A] rounded-xl flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                <circle cx="7" cy="17" r="2"/>
                <path d="M9 17h6"/>
                <circle cx="17" cy="17" r="2"/>
              </svg>
            </div>
            <span className="text-lg font-semibold text-gray-900">AutoFrais</span>
          </div>
          <button
            onClick={onMobileClose}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Close navigation menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav content — close drawer on link click */}
        <div className="flex-1 overflow-y-auto">
          <SidebarContent onNavClick={onMobileClose} />
        </div>
      </aside>
    </>
  );
};
